/**
 * @fileoverview Customer Create Page
 * @task B.INV.7 - Customer Management
 * @theme Polished Corporate - Teal accents, refined shadows
 */

import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@components/ui/button';

interface CustomerFormData {
  name: string;
  email: string;
  phone: string;
  website: string;
  taxId: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
  paymentTerms: string;
  creditLimit: string;
  notes: string;
}

/**
 * CustomerCreatePage Component
 * Form to create a new customer
 */
export function CustomerCreatePage(): JSX.Element {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<CustomerFormData>({
    name: '',
    email: '',
    phone: '',
    website: '',
    taxId: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'US',
    paymentTerms: 'net-30',
    creditLimit: '',
    notes: '',
  });

  const handleSubmit = (e: React.FormEvent): void => {
    e.preventDefault();
    // TODO: Implement customer creation API call
    console.log('Creating customer:', formData);
    navigate('/accounts-receivable/customers');
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ): void => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const states = [
    { value: '', label: 'Select State' },
    { value: 'AL', label: 'Alabama' },
    { value: 'AK', label: 'Alaska' },
    { value: 'AZ', label: 'Arizona' },
    { value: 'CA', label: 'California' },
    { value: 'CO', label: 'Colorado' },
    { value: 'CT', label: 'Connecticut' },
    { value: 'FL', label: 'Florida' },
    { value: 'GA', label: 'Georgia' },
    { value: 'IL', label: 'Illinois' },
    { value: 'MA', label: 'Massachusetts' },
    { value: 'NY', label: 'New York' },
    { value: 'TX', label: 'Texas' },
    { value: 'WA', label: 'Washington' },
  ];

  return (
    <div className="min-h-screen bg-gray-50/30">
      {/* Page Header - Polished Corporate */}
      <div className="bg-white shadow-sm">
        <div className="container py-6">
          <Link
            to="/accounts-receivable/customers"
            className="mb-4 inline-flex items-center text-sm text-gray-500 transition-colors hover:text-teal-600"
          >
            <svg className="mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Customers
          </Link>
          <div className="flex items-center gap-2 text-sm text-gray-400">
            <span>Accounts Receivable</span>
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            <span>Customers</span>
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            <span className="font-medium text-teal-600">Add New</span>
          </div>
          <h2 className="mt-1 text-2xl font-bold text-gray-900">Add New Customer</h2>
          <p className="mt-1 text-gray-500">Enter customer information</p>
        </div>
      </div>

      {/* Form */}
      <div className="container py-6">
        <form onSubmit={handleSubmit} className="mx-auto max-w-3xl">
          <div className="overflow-hidden rounded-xl bg-white shadow-card">
            {/* Company Information */}
            <div className="border-b border-gray-100 p-6">
              <h3 className="mb-4 font-semibold text-gray-900">Customer Information</h3>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <label htmlFor="name" className="mb-1.5 block text-sm font-medium text-gray-700">
                    Customer Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="e.g., Global Tech Industries"
                    className="h-10 w-full rounded-xl border border-gray-200 bg-gray-50/50 px-3 text-sm transition-all focus:border-teal-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-teal-500/20"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="mb-1.5 block text-sm font-medium text-gray-700">
                    Email *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="billing@customer.com"
                    className="h-10 w-full rounded-xl border border-gray-200 bg-gray-50/50 px-3 text-sm transition-all focus:border-teal-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-teal-500/20"
                  />
                </div>
                <div>
                  <label htmlFor="phone" className="mb-1.5 block text-sm font-medium text-gray-700">
                    Phone
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="+1 (555) 123-4567"
                    className="h-10 w-full rounded-xl border border-gray-200 bg-gray-50/50 px-3 text-sm transition-all focus:border-teal-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-teal-500/20"
                  />
                </div>
                <div>
                  <label htmlFor="website" className="mb-1.5 block text-sm font-medium text-gray-700">
                    Website
                  </label>
                  <input
                    type="url"
                    id="website"
                    name="website"
                    value={formData.website}
                    onChange={handleChange}
                    placeholder="https://www.customer.com"
                    className="h-10 w-full rounded-xl border border-gray-200 bg-gray-50/50 px-3 text-sm transition-all focus:border-teal-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-teal-500/20"
                  />
                </div>
                <div>
                  <label htmlFor="taxId" className="mb-1.5 block text-sm font-medium text-gray-700">
                    Tax ID / EIN
                  </label>
                  <input
                    type="text"
                    id="taxId"
                    name="taxId"
                    value={formData.taxId}
                    onChange={handleChange}
                    placeholder="XX-XXXXXXX"
                    className="h-10 w-full rounded-xl border border-gray-200 bg-gray-50/50 px-3 text-sm transition-all focus:border-teal-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-teal-500/20"
                  />
                </div>
              </div>
            </div>

            {/* Address */}
            <div className="border-b border-gray-100 p-6">
              <h3 className="mb-4 font-semibold text-gray-900">Billing Address</h3>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <label htmlFor="address" className="mb-1.5 block text-sm font-medium text-gray-700">
                    Street Address
                  </label>
                  <input
                    type="text"
                    id="address"
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    placeholder="100 Innovation Way, Suite 200"
                    className="h-10 w-full rounded-xl border border-gray-200 bg-gray-50/50 px-3 text-sm transition-all focus:border-teal-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-teal-500/20"
                  />
                </div>
                <div>
                  <label htmlFor="city" className="mb-1.5 block text-sm font-medium text-gray-700">
                    City
                  </label>
                  <input
                    type="text"
                    id="city"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    placeholder="Boston"
                    className="h-10 w-full rounded-xl border border-gray-200 bg-gray-50/50 px-3 text-sm transition-all focus:border-teal-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-teal-500/20"
                  />
                </div>
                <div>
                  <label htmlFor="state" className="mb-1.5 block text-sm font-medium text-gray-700">
                    State
                  </label>
                  <select
                    id="state"
                    name="state"
                    value={formData.state}
                    onChange={handleChange}
                    className="h-10 w-full rounded-xl border border-gray-200 bg-gray-50/50 px-3 text-sm transition-all focus:border-teal-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-teal-500/20"
                  >
                    {states.map((state) => (
                      <option key={state.value} value={state.value}>
                        {state.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="zipCode" className="mb-1.5 block text-sm font-medium text-gray-700">
                    ZIP Code
                  </label>
                  <input
                    type="text"
                    id="zipCode"
                    name="zipCode"
                    value={formData.zipCode}
                    onChange={handleChange}
                    placeholder="02108"
                    className="h-10 w-full rounded-xl border border-gray-200 bg-gray-50/50 px-3 text-sm transition-all focus:border-teal-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-teal-500/20"
                  />
                </div>
                <div>
                  <label htmlFor="country" className="mb-1.5 block text-sm font-medium text-gray-700">
                    Country
                  </label>
                  <select
                    id="country"
                    name="country"
                    value={formData.country}
                    onChange={handleChange}
                    className="h-10 w-full rounded-xl border border-gray-200 bg-gray-50/50 px-3 text-sm transition-all focus:border-teal-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-teal-500/20"
                  >
                    <option value="US">United States</option>
                    <option value="CA">Canada</option>
                    <option value="MX">Mexico</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Payment & Credit Information */}
            <div className="border-b border-gray-100 p-6">
              <h3 className="mb-4 font-semibold text-gray-900">Payment & Credit</h3>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="paymentTerms" className="mb-1.5 block text-sm font-medium text-gray-700">
                    Default Payment Terms
                  </label>
                  <select
                    id="paymentTerms"
                    name="paymentTerms"
                    value={formData.paymentTerms}
                    onChange={handleChange}
                    className="h-10 w-full rounded-xl border border-gray-200 bg-gray-50/50 px-3 text-sm transition-all focus:border-teal-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-teal-500/20"
                  >
                    <option value="due-on-receipt">Due on Receipt</option>
                    <option value="net-15">Net 15</option>
                    <option value="net-30">Net 30</option>
                    <option value="net-45">Net 45</option>
                    <option value="net-60">Net 60</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="creditLimit" className="mb-1.5 block text-sm font-medium text-gray-700">
                    Credit Limit (USD)
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">$</span>
                    <input
                      type="number"
                      id="creditLimit"
                      name="creditLimit"
                      value={formData.creditLimit}
                      onChange={handleChange}
                      min="0"
                      step="1000"
                      placeholder="50,000"
                      className="h-10 w-full rounded-xl border border-gray-200 bg-gray-50/50 pl-7 pr-3 text-sm transition-all focus:border-teal-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-teal-500/20"
                    />
                  </div>
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="notes" className="mb-1.5 block text-sm font-medium text-gray-700">
                    Notes
                  </label>
                  <textarea
                    id="notes"
                    name="notes"
                    value={formData.notes}
                    onChange={handleChange}
                    rows={3}
                    placeholder="Internal notes about this customer"
                    className="w-full rounded-xl border border-gray-200 bg-gray-50/50 px-3 py-2 text-sm transition-all focus:border-teal-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-teal-500/20"
                  />
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center justify-end gap-3 p-6">
              <Link to="/accounts-receivable/customers">
                <Button
                  variant="outline"
                  type="button"
                  className="rounded-xl border-gray-200 text-gray-600 hover:bg-gray-50"
                >
                  Cancel
                </Button>
              </Link>
              <Button
                type="submit"
                className="rounded-xl bg-gradient-to-r from-teal-500 to-teal-600 text-white shadow-sm transition-all hover:from-teal-600 hover:to-teal-700 hover:shadow-md"
              >
                <svg className="mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Create Customer
              </Button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
